<template>
    <!-- Einfacher Bereich: eine klickbare Zeile, keine leere große Karte //// -->
    <button v-if="!isRepeatable"
            type="button"
            class="editor-section-row"
            @click="handleSubPanelOpen">
        <v-icon icon="mdi-folder-outline" />
        <span class="editor-section-row__body">
            <span class="editor-section-row__title d-block">{{ props.label }}</span>
            <span v-if="props.hint && props.hint !== props.label"
                  class="editor-section-row__hint d-block">{{ props.hint }}</span>
        </span>
        <v-icon icon="mdi-chevron-right" />
    </button>

    <!-- Wiederholbare Liste ///////////////////////////////////////////////// -->
    <editor-field-frame v-else class="editor-field-frame--section"
                        :label="props.label" :hint="props.hint">
        <v-card class="editor-section-card" variant="flat">
            <v-card-text>
                <p v-if="!value || value.length === 0" class="text-medium-emphasis mb-0">
                    {{ $t('editor.fields.list.empty') }}
                </p>

                <draggable v-else
                           :list="value"
                           :item-key="((item) => value.indexOf(item))"
                           :disabled="!child_sortable"
                           handle=".drag-handle">
                    <template #item="{element, index}">
                        <div class="editor-repeater-item">
                            <v-icon v-if="child_sortable" class="drag-handle" :title="$t('common.actions.move')">
                                mdi-drag-vertical
                            </v-icon>

                            <button type="button"
                                    class="editor-repeater-item__title"
                                    :title="$t('common.actions.edit')"
                                    @click="handleSubPanelOpenWithProps({repeater_index: index})">
                                {{ getFirstChildLabel(index) }}
                            </button>

                            <!-- Beschriftete Aktion statt reinem Icon -->
                            <v-btn variant="text" size="small" density="comfortable"
                                   class="normal-case"
                                   append-icon="mdi-chevron-right"
                                   @click="handleSubPanelOpenWithProps({repeater_index: index})">
                                {{ $t('common.actions.edit') }}
                            </v-btn>

                            <!-- Sortieralternative zu Drag-and-drop -->
                            <v-menu v-if="child_sortable || child_deletable">
                                <template v-slot:activator="{props: menuProps}">
                                    <v-btn v-bind="menuProps" variant="text" size="small"
                                           density="comfortable" icon="mdi-dots-vertical"
                                           :title="$t('common.actions.more_actions')" />
                                </template>
                                <v-list density="compact">
                                    <v-list-item v-if="child_sortable" :disabled="index === 0"
                                                 prepend-icon="mdi-arrow-up"
                                                 @click="move(index, -1)">{{ $t('editor.fields.list.move_up') }}</v-list-item>
                                    <v-list-item v-if="child_sortable" :disabled="index === value.length - 1"
                                                 prepend-icon="mdi-arrow-down"
                                                 @click="move(index, 1)">{{ $t('editor.fields.list.move_down') }}</v-list-item>
                                    <v-list-item v-if="child_deletable"
                                                 prepend-icon="mdi-trash-can-outline"
                                                 @click="askToRemove(index)">{{ $t('common.actions.remove') }}</v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                    </template>
                </draggable>
            </v-card-text>
            <v-card-actions v-if="child_creatable">
                <v-btn variant="text" color="primary" prepend-icon="mdi-plus"
                       class="normal-case" @click="addToList">{{ $t('editor.fields.list.add_item') }}</v-btn>
            </v-card-actions>
        </v-card>

        <!-- Löschen immer mit Bestätigung /////////////////////////////////// -->
        <v-dialog v-model="showRemoveDialog" max-width="460">
            <v-card>
                <v-card-title>{{ $t('editor.fields.list.remove_title') }}</v-card-title>
                <v-card-text>
                    {{ $t('editor.fields.list.remove_text', {label: removeLabel}) }}
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" class="normal-case" @click="showRemoveDialog = false">{{ $t('common.actions.cancel') }}</v-btn>
                    <v-btn color="error" variant="flat" class="normal-case" @click="confirmRemove">{{ $t('common.actions.remove') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </editor-field-frame>
</template>

<script setup>
import {computed, ref} from "vue";
import draggable from "vuedraggable";
import jp from "jsonpath";
import EditorFieldFrame from "./EditorFieldFrame.vue";
import {mandatory_props, useEditorField} from "../../utils/editorConfigFieldHelpers";

const props = defineProps(mandatory_props);
const {store, jsonPath, value} = useEditorField(props);

/**
 * Ein SubPanel ist heute gleichzeitig Gruppe, Unterseite und Repeater. Ob es
 * wiederholbar ist, ergibt sich aus dem aktuellen Datenwert.
 * (Bekannter Sonderfall - siehe .editors/EDITOR-BAUKASTEN-UND-GESTALTUNG.md)
 */
const isRepeatable = computed(() => Array.isArray(value.value));

const child_creatable = computed(() => props.component_config?.child_creatable !== false);
const child_sortable = computed(() => props.component_config?.child_sortable !== false);
const child_deletable = computed(() => props.component_config?.child_deletable !== false);

function handleSubPanelOpenWithProps(myprops) {
    store.openSubPanel({
        window_index: props.component_config.window_target_index,
        json_path: jsonPath.value,
        window_title: props.label,
        ...myprops,
    });
}

function handleSubPanelOpen() {
    store.openSubPanel({
        window_index: props.component_config.window_target_index,
        window_title: props.label,
    });
}

function getFirstChildLabel(index) {
    const fallback = `${props.label} ${index + 1}`;

    if ( ! props.component_config?.child_label) return fallback;

    try {
        const path = `${jsonPath.value}[${index}].${props.component_config.child_label}`;
        const child_label = jp.value(store[props.jsonKey], path);

        if (child_label) return child_label;
    } catch (e) {
        // Fehlendes child_label ist ein bekannter Sonderfall - sichtbarer
        // Fallback statt leerer Zeile.
        console.warn('child_label konnte nicht gelesen werden', e);
    }

    return fallback;
}

/**
 * The panel list of the editor this SubPanel belongs to
 * (the info editor keeps its panels in "window_info").
 */
function editorPanels() {
    if (props.jsonKey === 'info' && Array.isArray(store.window_info)) return store.window_info;
    return store.window_items || [];
}

/**
 * Build an empty item out of the fields that are configured *inside* this
 * SubPanel. Beats an empty object: the child fields and the child_label work
 * right away instead of showing an item without any content.
 */
function blueprintFromConfig() {
    const items = editorPanels()[props.component_config?.window_target_index];
    if ( ! Array.isArray(items)) return null;

    const blueprint = {};
    items.forEach(item => {
        const path = item.json_path;

        // only simple relative keys ("title") are direct children of an item
        if (typeof path === 'string' && path && path !== '$'
            && ! path.includes('.') && ! path.includes('[')) {
            blueprint[path] = null;
        }
    });

    return Object.keys(blueprint).length ? blueprint : null;
}

function addToList() {
    let new_item = {};

    if ( ! Array.isArray(value.value)) {
        value.value = [];
    }

    if (props.component_config.child_blueprint) {
        new_item = Object.assign({}, JSON.parse(JSON.stringify(props.component_config.child_blueprint)));
    } else if (value.value.length > 0) {
        for (const key of Object.keys(value.value[0])) {
            new_item[key] = null;
        }
    } else {
        new_item = blueprintFromConfig() || {};
    }

    const repeater_index = value.value.push(new_item);
    handleSubPanelOpenWithProps({repeater_index: repeater_index - 1});
}

/**
 * Tastatur- und Screenreader-freundliche Alternative zu Drag-and-drop.
 */
function move(index, delta) {
    const target = index + delta;
    if (target < 0 || target >= value.value.length) return;

    const [item] = value.value.splice(index, 1);
    value.value.splice(target, 0, item);
}

// ----------------------------------------------------------------------------
// Entfernen mit Bestätigung
// ----------------------------------------------------------------------------
const showRemoveDialog = ref(false);
const removeIndex = ref(null);
const removeLabel = ref('');

function askToRemove(index) {
    removeIndex.value = index;
    removeLabel.value = getFirstChildLabel(index);
    showRemoveDialog.value = true;
}

function confirmRemove() {
    if (removeIndex.value !== null) {
        value.value.splice(removeIndex.value, 1);
    }

    removeIndex.value = null;
    showRemoveDialog.value = false;
}
</script>

<style scoped>
.editor-repeater-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    border: 1px solid var(--editor-border-color);
    border-radius: var(--editor-radius);
    margin-bottom: 8px;
}

.editor-repeater-item__title {
    flex: 1 1 auto;
    min-width: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.editor-repeater-item__title:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 3px;
    border-radius: 2px;
}
</style>
